/**
 * ArchLang — ProcessModel IR builder (v0.8 process language)
 *
 * Builds the resolved per-process {@link ProcessModel} IR from the process AST,
 * and is the ONLY place callers are resolved. The AST keeps the written form —
 * an omitted head stays `from: []`, a `|` stays a fan, a `\` stays a snake — so
 * the syntactic form survives for a serialize-back round-trip, and every
 * consumer that wants the resolved answer reads `hop.caller` here.
 *
 * Phase 2 builds + attaches `processModels`/`subprocessModels` to the model;
 * Phase 3 migrates the AST consumers (graph/validation/stubs/render/LSP) onto it.
 *
 * @see process-model.ts   — the IR types this populates
 * @see resolver.ts        — `iterProcessScopes` (raw decl + ownerPath source)
 * @see process-refs.ts    — `classifyCallee` (the shared callee classifier)
 */

import { buildIndexes } from "./graph.js";
import { iterProcessScopes } from "./resolver.js";
import type { ResolvedModel } from "./resolver.js";
import { classifyCallee } from "./process-refs.js";
import type {
  AwaitStep,
  BlockStep,
  DoStep,
  EachStep,
  FailStep,
  FinishStep,
  GoStep,
  IfStep,
  MergeBool,
  Name,
  NoteStep,
  OwnerBlockStep,
  ParallelStep,
  ProcessDecl,
  ProcessStep,
  QualifiedName,
  ReversibleStep,
  SelectStep,
  SimpleStep,
  SourceSpan,
  StepOwner,
  SubprocessDecl,
  TryStep,
  UnwindClause,
} from "./types.js";
import type {
  BlockNode,
  BoolExpr,
  CallHop,
  CallNode,
  CatchClause as IrCatch,
  Flow,
  LoopHead,
  MergeClause as IrMerge,
  NamedRef,
  Owner,
  ParallelBody,
  ProcessModel,
  ProcessNode,
  ResolvedCallee,
  ReversibleItem as IrReversibleItem,
  SelectCount as IrSelectCount,
  StepId,
  SubprocessModel,
  TerminatorNode,
  Unwind,
  Verbatim,
} from "./process-model.js";

// =============================================================================
// Public entry
// =============================================================================

/**
 * Build the per-process / per-subprocess IR for a model. `topLevelProcesses` and
 * `topLevelSubprocesses` are the RAW (parsed, pre-fill) top-level decls; module-
 * and process-nested decls are reached through `model.modules` (also raw).
 */
/**
 * Per-decl IR memoization (incremental path only). A `ProcessModel` is a pure
 * function of its `decl` object + the callee-classification verdicts (which only
 * change when the module/interface symbol STRUCTURE flips). So a cache keyed by
 * `(decl identity, ownerKey)` and validated against a monotonic structure
 * version is correct: a process-body or value edit replaces only the edited
 * decl object (and never bumps the version), so every UNCHANGED process reuses
 * its IR instead of rebuilding (build is ~13µs but ×1000 procs = ~13ms/edit).
 *
 * Subprocesses are template-stamped onto every instance — the SAME decl object
 * built under different `ownerPath`s yields different IR — so the ownerKey is
 * part of the cache key. The cold path passes no cache (always builds fresh),
 * keeping `assembleModel` output identical (the equivalence invariant).
 */
export interface ProcessModelCache {
  process(decl: ProcessDecl, ownerKey: string, build: () => ProcessModel): ProcessModel;
  subprocess(decl: SubprocessDecl, ownerKey: string, build: () => SubprocessModel): SubprocessModel;
}

export function buildProcessModels(
  model: ResolvedModel,
  topLevelProcesses: readonly ProcessDecl[],
  topLevelSubprocesses: readonly SubprocessDecl[],
  cache?: ProcessModelCache
): { processModels: ProcessModel[]; subprocessModels: SubprocessModel[] } {
  const indexes = buildIndexes(model);
  const callee = {
    modules: indexes.modulesByPath,
    interfaces: indexes.interfacesByPath,
    defaultInterfaceOf: indexes.defaultInterfaceOf,
  };
  const ownerKey = (p: QualifiedName | undefined): string => (p ?? []).join(".");
  // v0.11: the IR-cache key carries the landed space — a placed decl whose
  // target moves spaces across edits must not replay a stale-space IR.
  // (`ownerKey` itself stays bare: presence lookups key on the bare path.)
  const cacheKey = (p: QualifiedName | undefined, s?: QualifiedName): string =>
    s && s.length > 0 ? `${s.join(".")}::${ownerKey(p)}` : ownerKey(p);

  // v0.9.0 §1.5: membership instance names at a place → the member module path,
  // so a plane process owned by that place resolves `active`/`standby` actors.
  const instancesFor = (p: QualifiedName | undefined): ReadonlyMap<string, QualifiedName> | undefined => {
    const at = model.presence?.instancesAt.get(ownerKey(p));
    if (!at || at.size === 0) return undefined;
    const m = new Map<string, QualifiedName>();
    for (const [inst, member] of at) m.set(inst, member.split("."));
    return m;
  };

  // v0.11 (D1a): declaring-file space for free-standing decls; scoped ones
  // take their owning module's landed space inside the scope walks.
  const spaceOfFile = (d: { source?: { file?: string } }): QualifiedName | undefined =>
    model.spaces && d.source?.file ? model.spaces.get(d.source.file) : undefined;

  const procScopes = iterProcessScopes(model.modules, topLevelProcesses, undefined, spaceOfFile);
  const processModels = procScopes.map((scope) => {
    const build = () =>
      buildProcessModel(scope.decl, scope.ownerPath, callee, instancesFor(scope.ownerPath), scope.space);
    return cache ? cache.process(scope.decl, cacheKey(scope.ownerPath, scope.space), build) : build();
  });

  const rawSubs = collectRawSubprocesses(model.modules, topLevelSubprocesses, spaceOfFile);
  // Process-NESTED subprocesses (`process P { subprocess X { … } }`) live on the
  // process decl, not in module bodies — collect them (recursing
  // subprocess-nested ones) so the IR's subprocessModels is COMPLETE. A process
  // isn't a module-owner, so they carry no ownerPath (unowned steps TODO, like a
  // free-standing subprocess); `ownerProcess` records the owning process's
  // identity instead, so scope resolution can tell two processes' same-name
  // private subprocesses apart. Mirrors the AST validator (validation.ts:789/816).
  for (const scope of procScopes) {
    const qualified =
      scope.ownerPath && scope.ownerPath.length > 0
        ? `${scope.ownerPath.join(".")}.${scope.decl.name}`
        : scope.decl.name;
    // v0.11 (D2, rev-1 F6): the discriminator is space-qualified when the
    // host process is spaced — two spaces' same-named root processes must
    // not adopt each other's private subprocesses. Must match
    // `walkerContextFor`'s activeIdentity byte-for-byte.
    const owner =
      scope.decl.id ??
      (scope.space && scope.space.length > 0 ? `${scope.space.join(".")}::${qualified}` : qualified);
    collectNestedSubprocesses(scope.decl.subprocesses, rawSubs, owner, scope.space);
  }

  const subprocessModels = rawSubs.map((s) => {
    const build = () => buildSubprocessModel(s.decl, s.ownerPath, callee, instancesFor(s.ownerPath), s.space);
    const built = cache ? cache.subprocess(s.decl, cacheKey(s.ownerPath, s.space), build) : build();
    // Annotated outside the cache — the cached body is identical either way;
    // only the provenance differs per collection site.
    return s.ownerProcess ? { ...built, ownerProcess: s.ownerProcess } : built;
  });

  return { processModels, subprocessModels };
}

type CalleeIndex = {
  modules: { has(k: string): boolean };
  interfaces: { has(k: string): boolean };
  defaultInterfaceOf?: { get(k: string): string | undefined };
};

// =============================================================================
// Per-decl
// =============================================================================

function buildProcessModel(
  decl: ProcessDecl,
  ownerPath: QualifiedName | undefined,
  callee: CalleeIndex,
  instances?: ReadonlyMap<string, QualifiedName>,
  space?: QualifiedName
): ProcessModel {
  const body = buildFlow(decl.steps, { defaultOwner: ownerPath, callee, processOwner: ownerPath, instances }, []);
  return {
    id: decl.id,
    name: decl.name,
    anonymous: decl.anonymous ?? false,
    ownerPath,
    // Carried so the node universe can give a process the same lifecycle window
    // a module gets. Without it a `@planned` process put its marker on the axis
    // and showed as planned in the inspector, while every selector — and so the
    // board — read it as live: a stop on the scrubber nothing reacted to.
    ...(decl.decorations && decl.decorations.length > 0 ? { decorations: decl.decorations } : {}),
    // v0.11 (D1a): landed identity space, conditional (spaceless byte-stable).
    ...(space && space.length > 0 ? { space } : {}),
    ...(decl.exported ? { exported: true } : {}),
    rootOwner: ownerPath,
    description: decl.description,
    body,
    ...(decl.fields ? { fields: decl.fields } : {}),
    // Own-declared aspects; applyCascade resolves owner-cascade over these.
    ...(decl.aspects ? { aspects: decl.aspects } : {}),
    source: decl.source,
    idSource: decl.idSource,
    nameSource: decl.nameSource,
  };
}

function buildSubprocessModel(
  decl: SubprocessDecl,
  ownerPath: QualifiedName | undefined,
  callee: CalleeIndex,
  instances?: ReadonlyMap<string, QualifiedName>,
  space?: QualifiedName
): SubprocessModel {
  // `on Caller` is the default owner for owner-omitted steps (§7) — seed it as
  // the inheritance root so control nodes inside a top-level `subprocess X on
  // Caller` resolve to Caller (not a spurious owner-TODO), matching the AST fill.
  const root = decl.onCaller ? ([decl.onCaller] as QualifiedName) : ownerPath;
  const body = buildFlow(decl.steps, { defaultOwner: root, callee, processOwner: root, instances }, []);
  return {
    id: decl.id,
    name: decl.name,
    params: decl.params.map((p) => ({ name: p })),
    templateParam: decl.onCaller ? { name: decl.onCaller, source: decl.onCallerSource } : undefined,
    ownerPath,
    // See `ProcessModel.decorations` — same reason, same failure without it.
    ...(decl.decorations && decl.decorations.length > 0 ? { decorations: decl.decorations } : {}),
    // v0.11 (D1a): landed identity space, conditional (spaceless byte-stable).
    ...(space && space.length > 0 ? { space } : {}),
    exported: decl.exported ?? false,
    description: decl.description,
    body,
    source: decl.source,
    idSource: decl.idSource,
    nameSource: decl.nameSource,
  };
}

/** A raw subprocess decl with its resolving ownerPath + landed space (v0.11). */
interface RawSub {
  decl: SubprocessDecl;
  ownerPath?: QualifiedName;
  ownerProcess?: string;
  space?: QualifiedName;
}

/** Raw subprocess decls reachable in a model, each with its resolving ownerPath. */
function collectRawSubprocesses(
  roots: ResolvedModel["modules"],
  topLevel: readonly SubprocessDecl[],
  spaceOfFile?: (d: { source?: { file?: string } }) => QualifiedName | undefined
): RawSub[] {
  const out: RawSub[] = [];
  for (const sp of topLevel) {
    const space = spaceOfFile?.(sp);
    out.push({ decl: sp, ownerPath: undefined, space });
    // A subprocess nested inside this one is scoped to it (not module-owned) —
    // collect it too so the IR's subprocessModels stays COMPLETE.
    collectNestedSubprocesses(sp.subprocesses, out, undefined, space);
  }
  const walk = (mods: ResolvedModel["modules"], parent: readonly string[]): void => {
    for (const m of mods) {
      const path = [...parent, m.name];
      for (const sp of m.body.subprocesses ?? []) {
        // Landed space = the owning module's (D1a).
        out.push({ decl: sp, ownerPath: path, space: m.space });
        collectNestedSubprocesses(sp.subprocesses, out, undefined, m.space);
      }
      walk(m.modules, path);
    }
  };
  walk(roots, []);
  return out;
}

/** Collect process-nested subprocesses (and their own nested subprocesses),
 *  ownerPath undefined — they are scoped to a process, not owned by a module.
 *  `ownerProcess` (the host process's identity) carries the scope instead;
 *  subprocess-nested ones inherit the same host (and its landed space). */
function collectNestedSubprocesses(
  subs: readonly SubprocessDecl[] | undefined,
  out: RawSub[],
  ownerProcess?: string,
  space?: QualifiedName
): void {
  for (const sp of subs ?? []) {
    out.push({ decl: sp, ownerPath: undefined, ownerProcess, space });
    collectNestedSubprocesses(sp.subprocesses, out, ownerProcess, space);
  }
}

// =============================================================================
// Owner resolution
// =============================================================================

interface Ctx {
  /** Nearest non-`dist` enclosing owner (the inheritance default). */
  readonly defaultOwner?: QualifiedName;
  readonly callee: CalleeIndex;
  /** The process's primary owner ("us"), stable across nesting — used for
   *  inbound detection (`Them > Us.x` = a waited-for event, PROCESS.md §1). */
  readonly processOwner?: QualifiedName;
  /** v0.9.0 §1.5: membership instance names at the process's owner place →
   *  the member module path. A single-segment caller/callee matching an
   *  instance resolves to the member (retaining the alias for display). */
  readonly instances?: ReadonlyMap<string, QualifiedName>;
}

/** v0.9.0 §1.5: substitute a leading membership-instance segment with the
 *  member module path (`standby` → `A`, `standby.status` → `A.status`),
 *  keeping the instance alias. Non-instance paths pass through unchanged. */
function substituteInstance(segs: QualifiedName, ctx: Ctx): { segs: QualifiedName; instance?: Name } {
  const head = segs[0];
  const member = head !== undefined ? ctx.instances?.get(head) : undefined;
  if (member) return { segs: [...member, ...segs.slice(1)], instance: head };
  return { segs };
}

/** Resolve a control node's syntactic owner against the context + previous sibling. */
function resolveOwner(owner: StepOwner | undefined, ctx: Ctx, prev: Owner | undefined): Owner {
  if (!owner) {
    // Omitted → inherit the nearest enclosing owner, else a TODO (`none`).
    return ctx.defaultOwner
      ? { form: "inherited", resolved: ctx.defaultOwner }
      : { form: "none" };
  }
  switch (owner.form) {
    case "explicit":
      return { form: "explicit", resolved: owner.path, source: owner.source };
    case "dist":
      return { form: "dist", source: owner.source };
    case "ditto":
      // `|` → previous sibling STEP's head, else the nearest enclosing owner,
      // else TODO (§4.13.4 rule 3). §1.5: carry the previous owner's instance
      // alias when the fan inherits it (display continuity).
      return {
        form: "ditto",
        resolved: prev?.resolved ?? ctx.defaultOwner,
        source: owner.source,
        ...(prev?.resolved && prev.instance ? { instance: prev.instance } : {}),
      };
    case "snake":
      // Unreachable: `\` is call-only (rule 4), so the parser never attaches it
      // to a control node — it reaches `resolveCallerPath` instead.
      return { form: "none" };
  }
}

/** Resolve a call/note caller (left of `>` / note actor) — never `dist`. */
function resolveCallerPath(
  from: QualifiedName,
  fromDitto: boolean | undefined,
  fromSnake: boolean | undefined,
  source: SourceSpan | undefined,
  ctx: Ctx,
  anchor: Anchor
): Owner {
  const prev = anchor.head;
  if (fromSnake) {
    // `\` continues the previous step's run from its TAIL. A tail is never
    // inherited from a container (§4.13.4 rule 3) — with nothing to continue the
    // caller stays empty and validation raises SNAKE_NO_TAIL.
    return {
      form: "snake",
      ...(anchor.tail ? { resolved: anchor.tail } : {}),
      source,
      // §1.5: the tail's membership alias, so a continuation of `active.p`
      // stays on `active`'s lane — the mirror of the ditto branch below.
      ...(anchor.tail && anchor.tailInstance ? { instance: anchor.tailInstance } : {}),
    };
  }
  if (fromDitto)
    return {
      form: "ditto",
      resolved: prev?.resolved ?? ctx.defaultOwner,
      source,
      ...(prev?.resolved && prev.instance ? { instance: prev.instance } : {}),
    };
  if (from.length === 0) {
    // Anonymous head — inherit, else TODO.
    return ctx.defaultOwner ? { form: "inherited", resolved: ctx.defaultOwner } : { form: "none" };
  }
  // v0.9.0 §1.5: a caller written as a membership instance name resolves to the
  // member module (edges + validation), keeping the alias for display.
  const sub = substituteInstance(from, ctx);
  return { form: "explicit", resolved: sub.segs, source, ...(sub.instance ? { instance: sub.instance } : {}) };
}

/** {@link resolveCallerPath} for a written call step (the glyph flags live on it). */
function resolveCaller(step: SimpleStep, ctx: Ctx, anchor: Anchor): Owner {
  return resolveCallerPath(step.from, step.fromDitto, step.fromSnake, step.fromSource, ctx, anchor);
}

/** The owner a node passes down to its children: itself unless `dist`/unresolved. */
function childOwner(owner: Owner, ctx: Ctx): QualifiedName | undefined {
  if (owner.form === "dist") return ctx.defaultOwner; // dist does not propagate
  return owner.resolved ?? ctx.defaultOwner;
}

/** The `prev` (previous-sibling owner) a `|` fan resolves against, after a
 *  node. A `dist` node — and an unresolved (`none`) one — contribute no
 *  inheritable owner, so they DON'T advance `prev` (it skips past them): an
 *  unresolved owner costs a step its head, never its tail (§4.13.4). Else the
 *  node's own resolved owner. */
function advancedPrev(prev: Owner | undefined, owner: Owner): Owner | undefined {
  return answersFan(owner) ? owner : prev;
}

/**
 * Whether a node's owner ANSWERS a following `|` — i.e. becomes the head the
 * fan reads. A `dist` owner and an unresolved one contribute no inheritable
 * owner, so the fan reads straight past them; a node with no owner at all (a
 * terminator) is likewise transparent to `|`.
 *
 * Exported because "what answers a fan" must be asked in exactly one place:
 * the IR builder resolves against it, and the editing guards decide from it
 * which line a mutation is about to re-anchor. Two spellings of this rule drift
 * (they already did — the guard stopped at terminators the builder reads past,
 * so a pin silently failed to fire).
 */
export function answersFan(owner: Owner | undefined): boolean {
  return !!owner && owner.form !== "dist" && !!owner.resolved && owner.resolved.length > 0;
}

/** The owner a node contributes to the anchor, or `undefined` when it has none
 *  (a terminator, prose, a bare block). */
export function ownerOfNode(node: ProcessNode): Owner | undefined {
  return "owner" in node ? (node as { owner: Owner }).owner : undefined;
}

/**
 * The previous sibling STEP a `|` fan and a `\` snake write against (§4.13.4).
 * They are mirrors: same anchor, opposite end of it — `|` takes its `head` (the
 * subject it resolved to), `\` its `tail` (the top module of its last callee).
 */
interface Anchor {
  readonly head: Owner | undefined;
  readonly tail: QualifiedName | undefined;
  /** v0.9.0 §1.5: the membership alias the tail was WRITTEN as (`active` in
   *  `active.Inner.i`), when it was one. A `\` continues the SAME deployment,
   *  so it must render on that deployment's lane (`mod:A#active`) rather than
   *  the bare member's — the mirror of the alias a `|` inherits from `head`. */
  readonly tailInstance?: string;
}

/** The tail a call contributes: the top module of its LAST hop's callee, as
 *  WRITTEN (`A > B.C.d` continues from `B`, §4.13.3), instance-substituted so a
 *  membership alias continues from the member module and not a phantom one. */
function callTail(node: CallNode, ctx: Ctx): { segs: QualifiedName; instance?: string } | undefined {
  const last = node.hops[node.hops.length - 1];
  if (!last) return undefined;
  // Always the FIRST written segment, instance-aliased or not: `active` in
  // `active.Inner.i` names the member module exactly as `A` does in
  // `A.Inner.i`, and substitution resolves it. Reading the callee's resolved
  // `modulePath` instead would continue from the NESTED module the alias
  // happens to reach through, which §4.13.3 encapsulates.
  const head = last.callee.text.text.split(".")[0];
  // No written text at all: nothing to continue from. (An instance alias can't
  // reach here — `callee.instance` is only set when a segment of this same
  // written text matched one.)
  if (!head) return undefined;
  // The alias rides along: it names the same deployment of the member module,
  // and the snake continues THAT deployment (§1.5 lane identity).
  return substituteInstance([head] as QualifiedName, ctx);
}

/**
 * Advance the anchor past a sibling node (§4.13.4 resolution rules 1-7):
 *
 * - a call sets both ends — head = hop 0's caller, tail = its last callee;
 * - a note answers `|` with its owner and is TRANSPARENT to `\`, so an
 *   annotation may sit mid-run without cutting it;
 * - prose / `do` / `go` answer neither and are transparent to both;
 * - every other action — a control construct, `await`, an owner block, a
 *   terminator, a `dist` node — is tail-less, so it CUTS a snake run while a
 *   `|` still reads past it to the last answerable head.
 */
function advanceAnchor(anchor: Anchor, node: ProcessNode | BlockNode, ctx: Ctx): Anchor {
  const withHead = (): Owner | undefined =>
    "owner" in node ? advancedPrev(anchor.head, (node as { owner: Owner }).owner) : anchor.head;
  switch (node.kind) {
    case "call": {
      const t = callTail(node as CallNode, ctx);
      return { head: withHead(), tail: t?.segs, ...(t?.instance ? { tailInstance: t.instance } : {}) };
    }
    case "note":
      return { head: withHead(), tail: anchor.tail, ...(anchor.tailInstance ? { tailInstance: anchor.tailInstance } : {}) };
    case "prose":
    case "description":
    case "splice": // `do`
    case "reentry": // `go`
      return anchor;
    case "block":
      // A chained saga forward (`Inv > Pay.charge > Ledger.record`) is wrapped in
      // a SYNTHETIC block — the run inside it is what the next item anchors on.
      // An authored `{ }` is a body boundary instead: its steps are not siblings
      // of what follows, so it answers like any other tail-less action.
      return (node as BlockNode).chain
        ? (node as BlockNode).body.reduce((a, n) => advanceAnchor(a, n, ctx), anchor)
        : { head: anchor.head, tail: undefined };
    default:
      return { head: withHead(), tail: undefined };
  }
}

// =============================================================================
// Flow / node building
// =============================================================================

/** Chain-group key: hops of `A > B > C` are flattened into consecutive simple
 *  steps that all share their `source.start` offset (the parser fixes the chain
 *  start for every hop). Distinct steps — even `;`-separated on one line — start
 *  at distinct offsets. `undefined` for a step with no source (never groups). */
function chainKey(step: ProcessStep): string | undefined {
  const s = (step as { source?: SourceSpan }).source;
  return s?.start ? `${s.file ?? ""}:${s.start.offset}` : undefined;
}

function buildFlow(
  steps: readonly ProcessStep[],
  ctx: Ctx,
  path: readonly string[],
  startAnchor?: Anchor
): Flow {
  const out: ProcessNode[] = [];
  // Both glyphs read the previous sibling in THIS body only — a fresh anchor per
  // body is what keeps them from reaching across a brace/`:` boundary, and makes
  // a construct's head a non-sibling of its first child (§4.13.4 rule 1). The
  // ONE exception is a synthetic chain wrapper, which is a line rather than a
  // body: it inherits the anchor its line was written against.
  let anchor: Anchor = startAnchor ?? { head: undefined, tail: undefined };
  const runs = new RunStamper(path);
  let i = 0;
  while (i < steps.length) {
    const step = steps[i]!;
    if (step.kind === "simple") {
      // Gather a call chain (`A > B > C`): consecutive simple steps sharing the
      // chain start offset → ONE multi-hop CallNode, so the IR round-trips the
      // chain as one line instead of N split steps.
      const key = chainKey(step);
      let j = i + 1;
      if (key !== undefined) {
        while (j < steps.length && steps[j]!.kind === "simple" && chainKey(steps[j]!) === key) j++;
      }
      const chain = steps.slice(i, j) as SimpleStep[];
      const node = buildCall(chain, ctx, [...path, `simple:${i}`], anchor);
      out.push(node);
      anchor = advanceAnchor(anchor, node, ctx);
      runs.observe(out, anchor);
      i = j;
    } else {
      const node = buildNode(step, ctx, [...path, `${step.kind}:${i}`], anchor);
      out.push(node);
      anchor = advanceAnchor(anchor, node, ctx);
      runs.observe(out, anchor);
      i++;
    }
  }
  return runs.finish(out);
}

/**
 * Marks each `\` run as the body is built: the call that opens it and the `\`
 * lines continuing it share a `runId`, and the head collects its continuations'
 * callee paths. Time then folds the run as the chain it means (§4.13.4) — done
 * once, here, rather than by any consumer re-deriving runs from adjacency (a
 * flattened trace flow has neighbours that were never siblings).
 *
 * Run membership is read off the ANCHOR rather than re-derived from node kinds,
 * so "what continues a run" cannot drift from `advanceAnchor`'s answer: the run
 * survives exactly the nodes transparent to `\` (a note, prose, `do`, `go`) and
 * breaks on any node that clears the tail.
 *
 * `runId` identifies a run WITHIN one body — every consumer must fold against
 * the run heads of the body it is folding, never an enclosing one.
 */
class RunStamper {
  private head = -1;
  /** Indices of the calls that OPEN a run. The members are not recorded here:
   *  time reads them off the flow it is actually folding, so keeping a second
   *  copy of "this run's tail" on the node could only drift from it (it did —
   *  a pruned trace left the node listing hops the fold no longer had). */
  private readonly heads = new Set<number>();
  constructor(private readonly path: readonly string[]) {}

  /** Called after each node is pushed, with the anchor it produced. */
  observe(flow: ProcessNode[], anchor: Anchor): void {
    const at = flow.length - 1;
    const node = flow[at];
    if (!node) return;
    if (node.kind === "call") {
      const call = node as CallNode;
      // A resolved `\` continues the open run. A dangling one has no caller to
      // continue FROM, so it opens a run at itself — `TODO > E.v` then `\ > F.u`
      // is still one chain, just one whose head is at the draft floor.
      if (call.owner.form === "snake" && call.owner.resolved && this.head >= 0) {
        this.heads.add(this.head);
        flow[at] = { ...call, runId: this.runId(this.head) };
        return;
      }
      this.head = at;
      return;
    }
    this.cut(anchor);
  }

  /** A sibling that is not itself stampable (an authored block). It still ends
   *  the run if it left no tail — the same test every non-call node gets. */
  cut(anchor: Anchor): void {
    // Any node that leaves no tail cut the run; a transparent one kept it.
    if (anchor.tail === undefined) this.head = -1;
  }

  finish(flow: ProcessNode[]): Flow {
    for (const at of this.heads) {
      const call = flow[at] as CallNode;
      flow[at] = { ...call, runId: this.runId(at), runHead: true };
    }
    return flow;
  }

  private runId(head: number): string {
    return `${this.path.join("/")}#run:${head}`;
  }
}

/** Build one step against the anchor its line was written against. Only a call
 *  can read the anchor's TAIL (`\` is call-only, §4.13.4 rule 4) — every other
 *  kind needs the head alone, so they take `prev`. */
function buildNode(step: ProcessStep, ctx: Ctx, path: readonly string[], anchor: Anchor): ProcessNode {
  const prev = anchor.head;
  const stepId = (extra?: string): StepId => ({ path, contentKey: `${step.kind}${extra ? ":" + extra : ""}` });
  switch (step.kind) {
    case "simple":
      return buildCall([step], ctx, path, anchor);
    case "note": {
      const owner = resolveCallerPath(
        step.actor ?? [],
        step.actorDitto,
        undefined, // a note is never a `\` snake — the glyph is call-only
        step.actorSource,
        ctx,
        { head: prev, tail: undefined }
      );
      return {
        kind: "note",
        owner,
        text: vb(step.text, step.source),
        ...(step.fields ? { fields: step.fields } : {}),
        stepId: stepId(),
        source: step.source,
      };
    }
    case "prose":
      // Ownerless positional annotation — contributes nothing to `prev`, so
      // a following `|` ditto still resolves against the last ACTION.
      return { kind: "prose", text: vb(step.text, step.span), stepId: stepId(), source: step.span };
    case "fail":
    case "finish":
      return buildTerminator(step, path);
    case "if":
      return buildIf(step, ctx, path, prev);
    case "select":
      return buildSelect(step, ctx, path, prev);
    case "each":
      return buildEach(step, ctx, path, prev);
    case "try":
      return buildGuard(step, ctx, path, prev);
    case "parallel":
      return buildParallel(step, ctx, path, prev);
    case "reversible":
      return buildReversible(step, ctx, path, prev);
    case "await": {
      const owner = resolveOwner(step.owner, ctx, prev);
      return { kind: "await", owner, target: vb(step.target, step.targetSource), stepId: stepId(), source: step.span };
    }
    case "do":
      return buildSplice(step, ctx, path, prev);
    case "go":
      return {
        kind: "reentry",
        target: { name: step.target, source: step.targetSource },
        ...(step.comment !== undefined ? { comment: vb(step.comment, step.commentSource) } : {}),
        stepId: stepId(step.target),
        source: step.span,
      };
    case "owner_block": {
      const owner = resolveOwner(step.owner, ctx, prev);
      const body = buildFlow(step.steps, { ...ctx, defaultOwner: childOwner(owner, ctx) }, path);
      return { kind: "owner_block", owner, body, stepId: stepId(), source: step.span };
    }
  }
}

/** Build one CallNode from a chain of one-or-more flattened hops (`A > B > C`).
 *  The chain owner is hop 0's caller; later hops carry their own (parser-set)
 *  caller — the top module of the previous callee. bind/`as` belong to the last
 *  hop (the parser already attached them there). */
function buildCall(chain: readonly SimpleStep[], ctx: Ctx, path: readonly string[], anchor: Anchor): CallNode {
  const first = chain[0]!;
  const last = chain[chain.length - 1]!;
  const owner = resolveCaller(first, ctx, anchor);
  const hops: CallHop[] = chain.map((step, h) => {
    // v0.9.0 §1.5: a callee written as a membership instance name resolves to the
    // member module before classification; the alias is kept for display and the
    // verbatim text (round-trip) stays as authored.
    const toSub = substituteInstance(step.to, ctx);
    const clsStep = toSub.instance ? { ...step, to: toSub.segs } : step;
    const cls = classifyCallee(clsStep, ctx.callee);
    const calleeText = step.to.join(".") + (step.toUnspecified ? "." : "");
    const callee: ResolvedCallee = {
      modulePath: cls.kind === "module-anon" ? cls.moduleSegs : cls.interfaceSegs.slice(0, -1),
      interfacePath: cls.interfaceSegs,
      anonymous: cls.kind === "module-anon",
      text: vb(calleeText, step.toSource),
      ...(toSub.instance ? { instance: toSub.instance } : {}),
    };
    // hop 0's caller is the resolved chain owner (handles ditto/inherited); a
    // later hop's caller is the previous callee's top module, threaded in by the
    // parser as raw text — so a mid-chain instance name (`active > standby > C`)
    // must be instance-substituted here too, else it derives a phantom edge from
    // a non-existent `standby` module (§1.5).
    const caller = h === 0 ? owner.resolved ?? [] : substituteInstance(step.from, ctx).segs;
    return {
      caller,
      callerSource: step.fromSource,
      callee,
      args: step.args?.map((a) => vb(a)),
      direction: callDirection(caller, callee.modulePath, ctx),
      stepId: { path: [...path, `hop:${h}`], contentKey: `call:${step.from.join(".")}>${step.to.join(".")}` },
    };
  });
  return {
    kind: "call",
    owner,
    hops,
    bind: last.bind ? { name: last.bind, source: last.bindSource } : undefined,
    asName: last.asName ? { name: last.asName, source: last.asNameSource } : undefined,
    comment: last.comment !== undefined ? vb(last.comment, last.commentSource) : undefined,
    stepId: { path, contentKey: `call:${first.to.join(".")}` },
    source: chainSpan(first, last),
  };
}

/** outbound = we act (`Us > Them.x`); inbound = a waited-for event
 *  (`Them > Us.x` — the callee is us, the caller is someone else). "Us" is the
 *  process owner; with no owner (a bare/choreographed draft) everything is
 *  outbound. PROCESS.md §1. */
function callDirection(caller: QualifiedName, calleeModule: QualifiedName, ctx: Ctx): "outbound" | "inbound" {
  const us = ctx.processOwner?.[0];
  if (us && calleeModule[0] === us && caller[0] !== us) return "inbound";
  return "outbound";
}

/** Span covering a whole chain: first hop's start → last hop's end. */
function chainSpan(first: SimpleStep, last: SimpleStep): SourceSpan | undefined {
  const a = first.source, b = last.source;
  if (!a) return b;
  if (!b) return a;
  return { start: a.start, end: b.end, file: a.file };
}

function buildTerminator(step: FailStep | FinishStep, path: readonly string[]): TerminatorNode {
  const reasonText = step.kind === "fail" ? step.reason : step.label;
  const reasonSource = step.kind === "fail" ? step.reasonSource : step.labelSource;
  return {
    kind: "terminator",
    outcome: step.kind,
    reason: reasonText !== undefined ? vb(reasonText, reasonSource) : undefined,
    ...(step.all ? { all: true } : {}),
    stepId: { path, contentKey: step.kind },
    source: step.span,
  };
}

function buildIf(step: IfStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined): ProcessNode {
  const owner = resolveOwner(step.owner, ctx, prev);
  const childCtx: Ctx = { ...ctx, defaultOwner: childOwner(owner, ctx) };
  // Each sub-body gets a DISTINCT path segment (`arm:i` / `else`) so a step's
  // stepId stays unique across arms — else two arms' leading steps collide and
  // a stepId-keyed mutation (`replaceStep`, reassign) mis-targets the wrong arm.
  const arms = step.branches.map((b, ai) => ({
    condition: b.condition !== undefined ? vb(b.condition, b.conditionSource) : undefined,
    body: b.steps ? buildFlow(b.steps, childCtx, [...path, `arm:${ai}`]) : undefined,
    source: b.span,
  }));
  const elseArm = step.elseBranch
    ? { body: step.elseBranch.steps ? buildFlow(step.elseBranch.steps, childCtx, [...path, "else"]) : undefined, source: step.elseBranch.span }
    : undefined;
  return { kind: "branch", owner, arms, elseArm, stepId: { path, contentKey: "branch" }, source: step.span };
}

function buildSelect(step: SelectStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined): ProcessNode {
  const owner = resolveOwner(step.owner, ctx, prev);
  const childCtx: Ctx = { ...ctx, defaultOwner: childOwner(owner, ctx) };
  const count: IrSelectCount = !step.count
    ? { kind: "all" }
    : step.count.kind === "one"
      ? { kind: "one" }
      : { kind: "n", n: step.count.n, source: step.count.source };
  const cases = step.cases.map((c, ci) => ({ label: vb(c.label, c.labelSource), body: buildFlow(c.steps, childCtx, [...path, `case:${ci}`]), source: c.span }));
  return { kind: "select", owner, count, head: step.head !== undefined ? vb(step.head, step.headSource) : undefined, cases, stepId: { path, contentKey: "select" }, source: step.span };
}

function buildEach(step: EachStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined): ProcessNode {
  const owner = resolveOwner(step.owner, ctx, prev);
  const childCtx: Ctx = { ...ctx, defaultOwner: childOwner(owner, ctx) };
  const head = loopHead(step.label, step.source);
  if (step.retry) {
    return {
      kind: "retry",
      owner,
      bound: head,
      attempt: buildFlow(step.retry.attempt, childCtx, [...path, "attempt"]),
      catches: step.retry.catches.map((c, ci) => irCatch(c, childCtx, [...path, `catch:${ci}`])),
      elseBody: step.retry.elseSteps ? buildFlow(step.retry.elseSteps, childCtx, [...path, "elseBody"]) : undefined,
      stepId: { path, contentKey: "retry" },
      source: step.span,
    };
  }
  return { kind: "loop", owner, head, body: buildFlow(step.steps, childCtx, path), stepId: { path, contentKey: "loop" }, source: step.span };
}

function buildGuard(step: TryStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined): ProcessNode {
  const owner = resolveOwner(step.owner, ctx, prev);
  const childCtx: Ctx = { ...ctx, defaultOwner: childOwner(owner, ctx) };
  return {
    kind: "guard",
    owner,
    attempt: buildFlow(step.trySteps, childCtx, [...path, "attempt"]),
    catches: step.catches.map((c, ci) => irCatch(c, childCtx, [...path, `catch:${ci}`])),
    stepId: { path, contentKey: "guard" },
    source: step.span,
  };
}

function irCatch(c: { label?: string; steps: readonly ProcessStep[]; span?: SourceSpan }, ctx: Ctx, path: readonly string[]): IrCatch {
  return { label: c.label !== undefined ? vb(c.label) : undefined, body: buildFlow(c.steps, ctx, path), source: c.span };
}

function buildParallel(step: ParallelStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined): ProcessNode {
  const owner = resolveOwner(step.owner, ctx, prev);
  const childCtx: Ctx = { ...ctx, defaultOwner: childOwner(owner, ctx) };
  const merge: IrMerge = {
    detached: step.merge?.out ?? false,
    policy: step.merge?.policy,
    threshold: !step.merge
      ? undefined
      : step.merge.bool
        ? (() => {
            const expr = boolExpr(step.merge.bool!);
            return { kind: "boolean" as const, expr, text: vb(boolExprText(expr)) };
          })()
        : step.merge.count !== undefined
          ? { kind: "count", n: step.merge.count, source: step.merge.countSource }
          : undefined,
    source: step.merge?.source,
  };

  let body: ParallelBody;
  if (step.fanOut) {
    body = { form: "fanOut", head: loopHead(step.fanOut.label, step.fanOut.source), body: buildFlow(step.fanOut.steps, childCtx, [...path, "fanout"]) };
  } else if (step.select) {
    const count: IrSelectCount = !step.selectCount
      ? { kind: "all" }
      : step.selectCount.kind === "one"
        ? { kind: "one" }
        : { kind: "n", n: step.selectCount.n, source: step.selectCount.source };
    body = { form: "select", count, branches: step.branches.map((b, bi) => irBranch(b, childCtx, [...path, `branch:${bi}`])) };
  } else {
    body = { form: "branches", branches: step.branches.map((b, bi) => irBranch(b, childCtx, [...path, `branch:${bi}`])) };
  }
  return {
    kind: "parallel",
    owner,
    merge,
    body,
    ...(step.awaitHead !== undefined
      ? {
          awaitClause: {
            head: vb(step.awaitHead, step.awaitHeadSource),
            ...(step.awaitAs !== undefined ? { asName: vb(step.awaitAs, step.awaitAsSource) } : {}),
            elseBody: step.elseSteps ? buildFlow(step.elseSteps, childCtx, path) : undefined,
          },
        }
      : {}),
    stepId: { path, contentKey: "parallel" },
    source: step.span,
  };
}

function irBranch(b: ParallelStep["branches"][number], ctx: Ctx, path: readonly string[]) {
  return {
    name: b.name ? { name: b.name, source: b.nameSource } : undefined,
    body: buildFlow(b.steps, ctx, path),
    ...(b.implicit ? { implicit: true } : {}),
    source: b.span,
  };
}

function boolExpr(b: MergeBool): BoolExpr {
  if (b.kind === "name") return { kind: "name", name: b.name, source: b.source };
  return { kind: b.kind, terms: b.terms.map(boolExpr) };
}

/** Canonical verbatim text of a merge boolean (`(name1 and name3) or name2`) —
 *  reconstructed from the structured expr; a nested sub-expression of the other
 *  operator is parenthesized. Lowercase `and`/`or` (PROCESS.md §5). */
function boolExprText(e: BoolExpr): string {
  if (e.kind === "name") return e.name;
  return e.terms
    .map((t) => (t.kind !== "name" && t.kind !== e.kind ? `(${boolExprText(t)})` : boolExprText(t)))
    .join(` ${e.kind} `);
}

function buildReversible(step: ReversibleStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined): ProcessNode {
  const owner = resolveOwner(step.owner, ctx, prev);
  const childCtx: Ctx = { ...ctx, defaultOwner: childOwner(owner, ctx) };
  // Thread the anchor across items — saga steps are siblings, so a `|` forward
  // reads the previous item's head and a `\` forward its tail. A CHAINED forward
  // is wrapped in a block; `advanceAnchor` looks inside it, so the next item
  // anchors on the chain's head/tail rather than falling back to the construct.
  let itemAnchor: Anchor = { head: undefined, tail: undefined };
  // Saga item forwards are ONE sibling list, so a `\` run spanning them is a run
  // like any other and must be stamped as one — otherwise time sums it where the
  // same steps written flat would nest. Stamped with the same `RunStamper` the
  // flat path uses, so run membership stays a single rule.
  const forwards: ProcessNode[] = [];
  const runs = new RunStamper([...path, "items"]);
  const staged: (IrReversibleItem & { fwdIndex: number; wrapper: BlockNode | undefined })[] = step.items.map((it, i) => {
    const itemPath = [...path, `item:${i}`];
    const forward = it.forward
      ? it.forward.kind === "block"
        ? buildBlock(it.forward, childCtx, itemPath, itemAnchor)
        : buildNode(it.forward, childCtx, itemPath, itemAnchor)
      : undefined;
    // A chained forward is held in a synthetic wrapper, so the STEP the run is
    // made of is the call inside it, not the wrapper.
    const wrapper = forward?.kind === "block" && (forward as BlockNode).chain ? (forward as BlockNode) : undefined;
    // An AUTHORED block is a body boundary, so it is not part of any run — it
    // only cuts one, exactly as it does in a flat body.
    const stamped: ProcessNode | undefined = wrapper
      ? wrapper.body[0]
      : forward && forward.kind !== "block"
        ? forward
        : undefined;
    let fwdIndex = -1;
    if (forward) {
      itemAnchor = advanceAnchor(itemAnchor, forward, childCtx);
      if (stamped) {
        fwdIndex = forwards.push(stamped) - 1;
        runs.observe(forwards, itemAnchor);
      } else {
        runs.cut(itemAnchor);
      }
    }
    return {
      fwdIndex,
      wrapper,
      forward,
      // §4.13.4 rule 5: an `unwind` is not a sibling of the items around it — a
      // `|` there reads the FORWARD action it compensates, and the
      // compensation's own callee never becomes the run's tail.
      unwind: it.unwind
        ? buildUnwind(it.unwind, childCtx, itemPath, { head: itemAnchor.head, tail: undefined })
        : undefined,
      asName: it.asName ? { name: it.asName, source: it.asNameSource } : undefined,
      source: it.span,
    };
  });
  // `finish` rewrites a run's HEAD in place (to attach its continuations), so
  // the items have to pick their forward back up from the stamped array.
  runs.finish(forwards);
  const items: IrReversibleItem[] = staged.map(({ fwdIndex, wrapper, ...item }) => {
    if (fwdIndex < 0) return item;
    const node = forwards[fwdIndex]!;
    return { ...item, forward: wrapper ? { ...wrapper, body: [node, ...wrapper.body.slice(1)] } : node };
  });
  return {
    kind: "reversible",
    owner,
    trigger: { text: step.trigger !== undefined ? vb(step.trigger, step.triggerSource) : undefined, explicit: step.trigger !== undefined },
    items,
    stepId: { path, contentKey: "reversible" },
    source: step.span,
  };
}

function buildUnwind(u: UnwindClause, ctx: Ctx, path: readonly string[], anchor: Anchor): Unwind {
  const a = u.action;
  const unwindPath = [...path, "unwind"];
  let action: Unwind["action"];
  if (a.kind === "block") action = buildBlock(a, ctx, unwindPath);
  else if (a.kind === "do") action = buildSplice(a, ctx, unwindPath, anchor.head);
  else if (a.kind === "fail" || a.kind === "finish") action = buildTerminator(a, unwindPath);
  else action = buildCall([a], ctx, unwindPath, anchor);
  return { action, source: u.source };
}

function buildBlock(b: BlockStep, ctx: Ctx, path: readonly string[], anchor?: Anchor): BlockNode {
  return {
    kind: "block",
    ...(b.chain ? { chain: true as const } : {}),
    // A synthetic chain wrapper holds the hops of ONE line, so the line's own
    // glyph resolves against the anchor OUTSIDE the wrapper. An authored block
    // is a body: it starts fresh (§4.13.4 rule 1).
    body: buildFlow(b.steps, ctx, path, b.chain ? anchor : undefined),
    stepId: { path, contentKey: "block" },
    source: b.span,
  };
}

function buildSplice(step: DoStep, ctx: Ctx, path: readonly string[], prev: Owner | undefined) {
  const templateCaller: Owner | undefined = step.templateCaller
    ? step.templateCaller.form === "ditto"
      ? { form: "ditto", resolved: prev?.resolved, source: step.templateCaller.source }
      : { form: "explicit", resolved: step.templateCaller.path, source: step.templateCaller.source }
    : undefined;
  return {
    kind: "splice" as const,
    target: step.target,
    targetSource: step.targetSource,
    args: step.args?.map((a) => vb(a)),
    templateCaller,
    ...(step.asName !== undefined
      ? { asName: { name: step.asName, ...(step.asNameSource ? { source: step.asNameSource } : {}) } }
      : {}),
    ...(step.comment !== undefined ? { comment: vb(step.comment, step.commentSource) } : {}),
    stepId: { path, contentKey: `do:${step.target.join(".")}` },
    source: step.span,
  };
}

// =============================================================================
// Small helpers
// =============================================================================

function vb(text: string, source?: SourceSpan): Verbatim {
  return source ? { text, source } : { text };
}

function loopHead(label: string | undefined, collection: string | undefined): LoopHead | undefined {
  if (label === undefined && collection === undefined) return undefined;
  return {
    ...(label !== undefined ? { label: vb(label) } : {}),
    ...(collection !== undefined ? { collection: vb(collection) } : {}),
  };
}

// `NamedRef` re-export guard (keeps the import used even if tree-shaken).
export type { NamedRef };
